import React, { useState, useRef } from 'react';
import { FaUpload, FaFileExcel, FaCheckCircle, FaTimesCircle, FaSpinner, FaTrash, FaEye, FaTimes, FaDownload, FaUsers } from 'react-icons/fa';
import axios from 'axios';

const ContactUpload = ({ token }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert('Please select a valid Excel file (.xlsx or .xls)');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
  if (!selectedFile) {
    alert('Please select a file first');
    return;
  }

  setIsUploading(true);
  const formData = new FormData();
  formData.append('file', selectedFile);

  try {
    const response = await axios.post(
      'https://api.visiontech2030.com/api/contacts/admin/upload',
      formData,
      {
        headers: {
          'Authorization': token,
        }
      }
    );
    
    setUploadResult(response.data);
    setSelectedFile(null);
    fileInputRef.current.value = '';
  } catch (error) {
    console.error('Upload error:', error);
    
    // Better error handling
    if (error.response?.status === 403) {
      alert('Access denied. Please ensure you have admin privileges.');
    } else if (error.response?.status === 401) {
      alert('Authentication failed. Please login again.');
    } else {
      setUploadResult({
        totalProcessed: 0,
        successfulUploads: 0,
        duplicates: 0,
        errors: 1,
        errorMessages: ['Upload failed. Please try again.']
      });
    }
  } finally {
    setIsUploading(false);
  }
};

  const fetchAllContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await axios.get(
        'https://api.visiontech2030.com/api/contacts/admin/all',
        {
          headers: { 'Authorization': token }
        }
      );
      setAllContacts(response.data);
      setShowContacts(true);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      alert('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;

    try {
      await axios.delete(
        `https://api.visiontech2030.com/api/contacts/admin/batch/${batchId}`,
        {
          headers: { 'Authorization': token }
        }
      );
      
      fetchAllContacts();
      alert('Batch deleted successfully');
    } catch (error) {
      console.error('Error deleting batch:', error);
      alert('Failed to delete batch');
    }
  };

  const downloadSampleFile = () => {
    const csvContent = "data:text/csv;charset=utf-8,Mobile,Name\n0712345678,John Doe\n0723456789,Jane Smith\n0734567890,Bob Johnson";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sample_contacts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredContacts = allContacts.filter(contact =>
    contact.mobileNumber.includes(searchTerm) ||
    (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    if (!acc[contact.batchId]) {
      acc[contact.batchId] = [];
    }
    acc[contact.batchId].push(contact);
    return acc;
  }, {});

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <FaUsers style={styles.headerIcon} />
          <div>
            <h1 style={styles.title}>Contact Management</h1>
            <p style={styles.subtitle}>Upload and manage contact lists for WhatsApp outreach campaigns</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIconContainer}>
            <FaUsers style={styles.statIcon} />
          </div>
          <div style={styles.statContent}>
            <h3 style={styles.statNumber}>{allContacts.length}</h3>
            <p style={styles.statLabel}>Total Contacts</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIconContainer}>
            <FaCheckCircle style={styles.statIcon} />
          </div>
          <div style={styles.statContent}>
            <h3 style={styles.statNumber}>{Object.keys(groupedContacts).length}</h3>
            <p style={styles.statLabel}>Upload Batches</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div style={styles.uploadSection}>
        <div style={styles.uploadCard}>
          <div style={styles.uploadHeader}>
            <FaFileExcel style={styles.excelIcon} />
            <div>
              <h2 style={styles.uploadTitle}>Upload New Contacts</h2>
              <p style={styles.uploadDescription}>Upload Excel files containing mobile numbers and names</p>
            </div>
          </div>
          
          <div style={styles.sampleSection}>
            <button onClick={downloadSampleFile} style={styles.sampleButton}>
              <FaDownload />
              <span>Download Sample Format</span>
            </button>
            <p style={styles.sampleNote}>Download the sample file to see the correct format</p>
          </div>

          <div style={styles.fileInputContainer}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={styles.hiddenInput}
              id="file-input"
            />
            <label htmlFor="file-input" style={styles.fileLabel}>
              <FaUpload style={styles.uploadIcon} />
              <div style={styles.fileLabelContent}>
                <h4 style={styles.fileLabelText}>
                  {selectedFile ? selectedFile.name : 'Choose Excel File'}
                </h4>
                <p style={styles.fileLabelSubtext}>
                  {selectedFile ? 'File ready for upload' : 'Click to browse or drag and drop your Excel file here'}
                </p>
              </div>
            </label>
          </div>

          {selectedFile && (
            <div style={styles.fileInfo}>
              <div style={styles.fileDetails}>
                <FaFileExcel style={styles.fileIcon} />
                <div>
                  <h4 style={styles.fileName}>{selectedFile.name}</h4>
                  <p style={styles.fileSize}>
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button onClick={resetUpload} style={styles.resetButton}>
                <FaTimes />
              </button>
            </div>
          )}

          <div style={styles.uploadActions}>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              style={{
                ...styles.uploadButton,
                ...((!selectedFile || isUploading) ? styles.disabledButton : {})
              }}
            >
              {isUploading ? (
                <>
                  <FaSpinner style={styles.spinner} />
                  <span>Processing Upload...</span>
                </>
              ) : (
                <>
                  <FaUpload />
                  <span>Upload Contacts</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Upload Results */}
        {uploadResult && (
          <div style={styles.resultCard}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>Upload Results</h3>
              <div style={styles.resultBadge}>
                Batch ID: {uploadResult.batchId}
              </div>
            </div>
            
            <div style={styles.resultStats}>
              <div style={styles.resultStat}>
                <FaCheckCircle style={styles.successIcon} />
                <div>
                  <h4 style={styles.resultNumber}>{uploadResult.successfulUploads}</h4>
                  <p style={styles.resultLabel}>Successful</p>
                </div>
              </div>
              <div style={styles.resultStat}>
                <FaTimesCircle style={styles.warningIcon} />
                <div>
                  <h4 style={styles.resultNumber}>{uploadResult.duplicates}</h4>
                  <p style={styles.resultLabel}>Duplicates</p>
                </div>
              </div>
              <div style={styles.resultStat}>
                <FaTimesCircle style={styles.errorIcon} />
                <div>
                  <h4 style={styles.resultNumber}>{uploadResult.errors}</h4>
                  <p style={styles.resultLabel}>Errors</p>
                </div>
              </div>
            </div>
            
            {uploadResult.errorMessages && uploadResult.errorMessages.length > 0 && (
              <div style={styles.errorSection}>
                <h4 style={styles.errorTitle}>Error Details:</h4>
                <div style={styles.errorList}>
                  {uploadResult.errorMessages.slice(0, 5).map((error, index) => (
                    <p key={index} style={styles.errorMessage}>{error}</p>
                  ))}
                  {uploadResult.errorMessages.length > 5 && (
                    <p style={styles.moreErrors}>
                      ... and {uploadResult.errorMessages.length - 5} more errors
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Contacts Section */}
      <div style={styles.viewSection}>
        <button
          onClick={fetchAllContacts}
          disabled={loadingContacts}
          style={{
            ...styles.viewButton,
            ...(loadingContacts ? styles.disabledButton : {})
          }}
        >
          {loadingContacts ? (
            <>
              <FaSpinner style={styles.spinner} />
              <span>Loading Contacts...</span>
            </>
          ) : (
            <>
              <FaEye />
              <span>View All Contacts ({allContacts.length})</span>
            </>
          )}
        </button>
      </div>

      {/* Contacts Modal */}
      {showContacts && (
        <div style={styles.modalOverlay} onClick={() => setShowContacts(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Contact Database ({allContacts.length} contacts)
              </h2>
              <button
                onClick={() => setShowContacts(false)}
                style={styles.closeButton}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={styles.searchSection}>
              <input
                type="text"
                placeholder="Search contacts by mobile or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            
            <div style={styles.contactsList}>
              {Object.keys(groupedContacts).length === 0 ? (
                <div style={styles.emptyState}>
                  <FaUsers style={styles.emptyIcon} />
                  <h3 style={styles.emptyTitle}>No contacts found</h3>
                  <p style={styles.emptyText}>Upload some contacts to get started</p>
                </div>
              ) : (
                Object.entries(groupedContacts).map(([batchId, contacts]) => (
                  <div key={batchId} style={styles.batchGroup}>
                    <div style={styles.batchHeader}>
                      <div style={styles.batchInfo}>
                        <h3 style={styles.batchTitle}>Batch: {batchId}</h3>
                        <span style={styles.batchCount}>{contacts.length} contacts</span>
                      </div>
                      <button
                        onClick={() => deleteBatch(batchId)}
                        style={styles.deleteBatchButton}
                        title="Delete entire batch"
                      >
                        <FaTrash />
                        <span>Delete Batch</span>
                      </button>
                    </div>
                    
                    <div style={styles.contactsGrid}>
                      {contacts.slice(0, 10).map((contact) => (
                        <div key={contact.id} style={styles.contactItem}>
                          <h4 style={styles.contactMobile}>{contact.mobileNumber}</h4>
                          {contact.name && (
                            <p style={styles.contactName}>{contact.name}</p>
                          )}
                          <div style={styles.contactMeta}>
                            <span>Added: {new Date(contact.createdAt).toLocaleDateString()}</span>
                            {contact.fetchCount > 0 && (
                              <span> • Fetched: {contact.fetchCount} times</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {contacts.length > 10 && (
                        <div style={styles.moreContacts}>
                          <p>+{contacts.length - 10} more contacts in this batch</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: '#f8fafc',
    minHeight: '100vh'
  },
  header: {
    marginBottom: '32px'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  },
  headerIcon: {
    fontSize: '40px',
    color: '#3b82f6'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '32px'
  },
  statCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  },
  statIconContainer: {
    width: '56px',
    height: '56px',
    backgroundColor: '#dbeafe',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statIcon: {
    fontSize: '24px',
    color: '#3b82f6'
  },
  statContent: {},
  statNumber: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  statLabel: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  uploadSection: {
    marginBottom: '32px'
  },
  uploadCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px'
  },
  uploadHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '28px'
  },
  excelIcon: {
    fontSize: '48px',
    color: '#10b981'
  },
  uploadTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  uploadDescription: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
  },
  sampleSection: {
    textAlign: 'center',
    marginBottom: '28px',
    padding: '20px',
    backgroundColor: '#f1f5f9',
    borderRadius: '12px'
  },
  sampleButton: {
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  sampleNote: {
    fontSize: '14px',
    color: '#64748b',
    margin: '12px 0 0 0'
  },
  fileInputContainer: {
    marginBottom: '24px'
  },
  hiddenInput: {
    display: 'none'
  },
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '32px',
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#f8fafc'
  },
  uploadIcon: {
    fontSize: '32px',
    color: '#3b82f6'
  },
  fileLabelContent: {},
  fileLabelText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  fileLabelSubtext: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  fileInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  fileDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  fileIcon: {
    fontSize: '24px',
    color: '#10b981'
  },
  fileName: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  fileSize: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  resetButton: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  uploadActions: {
    display: 'flex',
    justifyContent: 'center'
  },
  uploadButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  spinner: {
    animation: 'spin 1s linear infinite'
  },
  resultCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  resultTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
  },
  resultBadge: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500'
  },
  resultStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '24px'
  },
  resultStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px'
  },
  resultNumber: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  resultLabel: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  successIcon: {
    fontSize: '24px',
    color: '#10b981'
  },
  warningIcon: {
    fontSize: '24px',
    color: '#f59e0b'
  },
  errorIcon: {
    fontSize: '24px',
    color: '#ef4444'
  },
  errorSection: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    padding: '20px'
  },
  errorTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#dc2626',
    margin: '0 0 12px 0'
  },
  errorList: {},
  errorMessage: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 8px 0'
  },
  moreErrors: {
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
    margin: 0
  },
  viewSection: {
    textAlign: 'center',
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  viewButton: {
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
  },
  closeButton: {
    backgroundColor: '#f1f5f9',
    border: 'none',
    color: '#64748b',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  searchSection: {
    padding: '24px',
    borderBottom: '1px solid #e2e8f0'
  },
  searchInput: {
    width: '100%',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '12px 16px',
    color: '#1e293b',
    fontSize: '14px',
    outline: 'none'
  },
  contactsList: {
    padding: '24px',
    maxHeight: '60vh',
    overflowY: 'auto'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    margin: '0 0 8px 0'
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  batchGroup: {
    marginBottom: '32px',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    overflow: 'hidden'
  },
  batchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f8fafc'
  },
  batchInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  batchTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
  },
  batchCount: {
    backgroundColor: '#dbeafe',
    color: '#3b82f6',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  deleteBatchButton: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  contactsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
    padding: '20px'
  },
  contactItem: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px'
  },
  contactMobile: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  contactName: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 8px 0'
 },
 contactMeta: {
   fontSize: '12px',
   color: '#6b7280',
   margin: 0
 },
 moreContacts: {
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
   padding: '24px',
   backgroundColor: '#f8fafc',
   border: '1px dashed #cbd5e1',
   borderRadius: '12px'
 }
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
 @keyframes spin {
   0% { transform: rotate(0deg); }
   100% { transform: rotate(360deg); }
 }
 
 .contact-upload-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
 }
 
 .contact-upload-button:hover:not(:disabled) {
   transform: translateY(-1px);
   box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
 }
 
 .contact-upload-file-label:hover {
   border-color: #3b82f6;
   background-color: #eff6ff;
 }
 
 .contact-upload-reset-button:hover {
   background-color: #fee2e2;
   transform: scale(1.05);
 }
 
 .contact-upload-sample-button:hover {
   background-color: #059669;
   transform: translateY(-1px);
 }
 
 .contact-upload-close-button:hover {
   background-color: #e2e8f0;
   color: #374151;
 }
`;
document.head.appendChild(styleSheet);

export default ContactUpload;
